import type { ClientCreditView } from "@vaulted/shared";
import { formatCents } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { errorMessage, get, post } from "../api";
import { useSession } from "../session";
import { Alert, Button, Dialog, Field, MoneyField, Panel, PanelHeader, formatDateTime } from "./ui";

const KIND_LABELS: Record<ClientCreditView["history"][number]["kind"], string> = {
  added: "Added",
  removed: "Taken off",
  used: "Used at checkout",
  returned: "Given back (payment refunded)",
  refund: "Refund as credit",
};

/** A client's account credit: the balance, its history, and (for managers) adding or taking it off. */
export function AccountCredit({ clientId }: { clientId: string }) {
  const { can } = useSession();
  const credit = useQuery({ queryKey: ["client-credit", clientId], queryFn: () => get<ClientCreditView>(`/api/clients/${clientId}/credit`) });
  const [changing, setChanging] = useState<"add" | "remove" | null>(null);
  const v = credit.data;
  if (!v) return null;
  const manage = can("payments.refund");
  return (
    <Panel>
      <PanelHeader
        title={`Account credit: ${formatCents(v.balanceCents)}`}
        description="Used at checkout (Account credit). Added by a manager, or when a refund is given as credit."
        action={
          manage && (
            <div className="flex gap-2">
              <Button variant="primary" onClick={() => setChanging("add")}>
                Add credit
              </Button>
              {v.balanceCents > 0 && (
                <Button variant="quiet" onClick={() => setChanging("remove")}>
                  Take off
                </Button>
              )}
            </div>
          )
        }
      />
      {v.history.length ? (
        <ul className="px-5 pb-3">
          {v.history.map((h) => (
            <li key={h.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-mist py-2.5 text-sm last:border-0">
              <span className="w-40 text-muted">{formatDateTime(h.at)}</span>
              <span className="min-w-0 flex-1">
                <span className="font-semibold">{KIND_LABELS[h.kind]}</span>
                {h.reason && <span className="text-ink-soft"> · {h.reason}</span>}
                {h.by && <span className="text-muted"> · {h.by}</span>}
              </span>
              <span className={`w-24 text-right font-semibold tabular-nums ${h.amountCents < 0 ? "text-ink-soft" : "text-ok"}`}>
                {h.amountCents < 0 ? "−" : "+"}
                {formatCents(Math.abs(h.amountCents))}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="px-5 pb-5 text-sm text-muted">No account credit.</p>
      )}
      {changing && <CreditDialog clientId={clientId} mode={changing} balanceCents={v.balanceCents} onClose={() => setChanging(null)} />}
    </Panel>
  );
}

function CreditDialog({ clientId, mode, balanceCents, onClose }: { clientId: string; mode: "add" | "remove"; balanceCents: number; onClose: () => void }) {
  const qc = useQueryClient();
  const [cents, setCents] = useState<number | null>(null);
  const [reason, setReason] = useState("");
  const save = useMutation({
    mutationFn: () => post<ClientCreditView>(`/api/clients/${clientId}/credit`, { amountCents: mode === "add" ? cents : -(cents ?? 0), reason }),
    onSuccess: (v) => (qc.setQueryData(["client-credit", clientId], v), onClose()),
  });
  const over = mode === "remove" && (cents ?? 0) > balanceCents;
  return (
    <Dialog
      title={mode === "add" ? "Add account credit" : "Take credit off"}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={save.isPending} disabled={!cents || cents <= 0 || over || reason.trim().length < 2} onClick={() => save.mutate()}>
            {mode === "add" ? "Add credit" : "Take off"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <MoneyField label="Amount" cents={cents} onChange={setCents} hint={mode === "remove" ? `They have ${formatCents(balanceCents)}.` : undefined} />
        <Field label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder={mode === "add" ? "e.g. Make-up for a missed add-on on 10/25" : "e.g. Added by mistake"} hint="Kept with the credit and in the audit log." />
        {over && <Alert>That's more than their credit.</Alert>}
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
      </div>
    </Dialog>
  );
}
